import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import generateSummaryPdf from './summaryPdf';
import CTAButton from '../../../CTAButton';
import Table from '../../../ComponentLibrary/oc-table';
import ChevronLink from '../../../OC/oc-chevron-link';
import { connectLocation } from '../../../location/LocationConnectors';
import { toR, roundOff } from '../assemblies/shared/constants';
import { ASSEMBLIES, GROUPS } from '../constants';
import { useTRC } from '../store';
import { ResultsCard, PdfIcon } from '../styles';

const fmtRSI = v => (typeof v === 'number' && Number.isFinite(v) ? roundOff(v, 2).toFixed(2) : '—');
const fmtR = v => (typeof v === 'number' && Number.isFinite(v) ? roundOff(toR(v), 2).toFixed(2) : '—');

/** map calcId → groupId for navigation */
const GROUP_BY_CALC = (() => {
  const map = {};
  GROUPS.forEach(g =>
    (g.items || []).forEach(it => {
      map[it.id] = g.id;
    })
  );
  return map;
})();

/** Build rows for an assembly result */
function buildRows(results, options = {}) {
  const { t } = options;
  if (!results) return [];

  const rows = [];

  if (results.effectiveAssemblyRSI != null)
    rows.push({
      label: t('thermal_calculator.effective_assembly_rsi'),
      rsi: results.effectiveAssemblyRSI,
    });

  if (results.centerOfCavityRSI != null)
    rows.push({
      label: t('thermal_calculator.center_of_cavity_r_value'),
      rsi: results.centerOfCavityRSI,
    });

  if (results.installedInsulationRSI != null)
    rows.push({
      label: t('thermal_calculator.installed_insulation_rsi'),
      rsi: results.installedInsulationRSI,
    });

  if (Array.isArray(results.summaryExtras)) {
    results.summaryExtras.forEach(ex => rows.push({ label: ex.label, rsi: ex.value, noR: !!ex.noR }));
  }

  return rows;
}

/** Single Assembly Card */
const AssemblyCard = ({ calcId, title, results, onModify, t }) => {
  const rows = useMemo(() => buildRows(results, { t }), [results]);

  return (
    <ResultsCard border="0" style={{ marginBottom: 20 }}>
      <div>
        <span style={{ fontSize: 20, fontWeight: 700 }}>{title}</span>
      </div>
      <Table striped bordered fixedTableLayout firstLeft margin="24px 0" textTransform="none">
        <thead>
          <tr>
            <th style={{ width: 390, textAlign: 'left' }}>{t('thermal_calculator.results_summary.etr')}</th>
            <th style={{ width: 88 }}>{t('thermal_calculator.rsi_only')}</th>
            <th style={{ width: 88 }}>R</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td>
                <b>{t('thermal_calculator.results_summary.no_results')}</b>
              </td>
              <td>—</td>
              <td>—</td>
            </tr>
          ) : (
            rows.map(r => (
              <tr key={`${calcId}-${r.label}`}>
                <td>
                  <b>{r.label}</b>
                </td>
                <td style={{ textAlign: 'center' }}>{fmtRSI(r.rsi)}</td>
                <td style={{ textAlign: 'center' }}>{r.noR ? '' : fmtR(r.rsi)}</td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      <div style={{ marginTop: 8 }}>
        <ChevronLink onClick={() => onModify(calcId, GROUP_BY_CALC[calcId])} style={{ fontSize: 18 }}>
          {t('thermal_calculator.results_summary.modify_calc')}
        </ChevronLink>
      </div>
    </ResultsCard>
  );
};

AssemblyCard.propTypes = {
  calcId: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types,react/require-default-props
  results: PropTypes.object, // may be null
  onModify: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

/** Main Summary Component */
const ResultsSummary = ({ onRequestModify, t }) => {
  const { state, resetResults: clearAll } = useTRC();
  const { province, region, hdd } = state.location;
  const { resultsByCalc } = state;

  const calcIds = Object.keys(ASSEMBLIES);

  return (
    <div>
      {/* Results Summary header */}
      <span style={{ fontSize: 32, fontWeight: 700 }}>{t('thermal_calculator.results_summary.main')}</span>
      <p style={{ margin: '16px 0 16px 0', lineHeight: 1.4 }}>
        {t('thermal_calculator.footnotes.consult_locations')}
        <a
          href="https://insulation.owenscorning.ca/builders/insulation-requirements/"
          target="_blank"
          rel="noopener noreferrer"
          style={{ marginLeft: 6 }}
        >
          {t('thermal_calculator.results_summary.naima')}
        </a>
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 64 }}>
        <CTAButton
          text={t('thermal_calculator.results_summary.download_results')}
          iconLeft
          beforeIcon={<PdfIcon />} // uses PdfIcon from styles.js
          onClick={() =>
            generateSummaryPdf({
              province,
              region,
              hdd,
              resultsByCalc: resultsByCalc || {},
              assemblies: ASSEMBLIES,
              t,
            })
          }
        />
        <CTAButton
          element="button"
          type="button"
          text={t('thermal_calculator.results_summary.reset_all')}
          onClick={clearAll}
          style={{
            minWidth: '120px',
            padding: '8px 16px',
            border: '1px solid #ccc',
            background: '#fff',
            color: '#000',
          }}
        />
      </div>

      {/* Results grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
          gap: 20,
        }}
      >
        {calcIds.map(id => (
          <AssemblyCard
            t={t}
            key={id}
            calcId={id}
            title={t(`thermal_calculator.titles.${ASSEMBLIES[id]}`)}
            results={resultsByCalc?.[id]?.results || null}
            onModify={onRequestModify}
          />
        ))}
      </div>
    </div>
  );
};

export default connectLocation(ResultsSummary);

ResultsSummary.propTypes = {
  onRequestModify: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};
